/**
 * Presentational pieces for the Host (Zoom) Select — per-option row,
 * trigger value, and a small zoom-status helper.
 *
 * Mirrors the legacy `SchZoomHost.tsx` chip variants:
 *   - "Paid user" / "Free user" / "Needs Zoom license"  → neutral
 *   - "Not part of Zoom account" / "Zoom limit reached" → error
 *
 * Caller (`useScheduleHostField`) attaches a `zoomStatus` blob on each
 * option's `data` so these renders can paint the right tag without
 * doing any data fetching.
 */
import { Avatar, Tag, type SelectOption } from '@kula-ai/components';
import { Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';

export interface HostZoomStatus {
  can_host_zoom_meeting?: boolean;
  zoom_rate_limit_reached?: boolean;
  /** Numeric Zoom plan: 1=Basic (Free), 2=Licensed (Paid), 4=Unassigned, 99=None. */
  zoom_plan?: number;
  /** Optional human label from BE; not currently used because we map by code below. */
  zoom_plan_type?: string;
}

export interface HostOptionData {
  zoomStatus?: HostZoomStatus;
  description?: string;
  image?: string;
}

interface ChipInfo {
  label: string;
  variant: 'success' | 'error' | 'neutral';
}

/**
 * Returns the chip to display for a given zoom status, or undefined if no
 * tag should render (e.g. status unknown). Mirrors `getZoomInfo` from the
 * legacy `SchZoomHost` so the wording matches existing user mental model.
 */
export function getHostZoomChip(
  status: HostZoomStatus | undefined
): ChipInfo | undefined {
  if (!status) return undefined;
  if (status.can_host_zoom_meeting === false) {
    return { label: 'Not part of Zoom account', variant: 'error' };
  }
  if (status.zoom_rate_limit_reached) {
    return { label: 'Zoom limit reached', variant: 'error' };
  }
  const plan = status.zoom_plan;
  if (plan === 4 || plan === 99) {
    return { label: 'Needs Zoom license', variant: 'neutral' };
  }
  if (plan === 2) {
    return { label: 'Paid user', variant: 'neutral' };
  }
  if (plan === 1) {
    return { label: 'Free user', variant: 'neutral' };
  }
  return undefined;
}

/**
 * `true` when the user behind the option can NOT actually host. Picked up by
 * the Select via `isOptionDisabled`. Same rule as legacy:
 *   - explicit `can_host_zoom_meeting === false` → disabled
 *   - `zoom_rate_limit_reached === true` → disabled
 */
export function isHostOptionDisabled(
  status: HostZoomStatus | undefined
): boolean {
  if (!status) return false;
  if (status.can_host_zoom_meeting === false) return true;
  if (status.zoom_rate_limit_reached === true) return true;
  return false;
}

/**
 * Read meta from the option. Each option carries `zoomStatus` and `image`
 * at the top level (the same shape `useScheduleHostField` produces). Some
 * code paths historically nested these under `data` — fall back to that
 * shape so a stale call site doesn't silently render unstyled options.
 */
function getMeta(option: SelectOption | undefined): HostOptionData | undefined {
  if (!option) return undefined;
  const flat = option as unknown as HostOptionData;
  if (flat.zoomStatus || flat.image) return flat;
  return option.data as HostOptionData | undefined;
}

export function renderHostOption(
  option: SelectOption,
  ctx: { isSelected: boolean }
) {
  const meta = getMeta(option);
  const chip = getHostZoomChip(meta?.zoomStatus);
  const disabled = isHostOptionDisabled(meta?.zoomStatus);
  // Image: prefer `option.image` (kula-ai SelectOption convention), fall
  // back to `data.image` for the legacy nested shape.
  const image =
    (option as { image?: string }).image ?? meta?.image ?? undefined;
  return (
    <Flex
      w="100%"
      alignItems="center"
      gap="3"
      opacity={disabled ? 0.5 : 1}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      data-selected={ctx.isSelected ? 'true' : undefined}
    >
      <Avatar src={image} name={option.label} size="sm" />
      <KText
        flex="1"
        minW={0}
        variant="small_text"
        color="secondary-900"
        fontWeight="500"
        noOfLines={1}
      >
        {option.label}
      </KText>
      {chip && <Tag label={chip.label} variant={chip.variant} size="sm" />}
    </Flex>
  );
}

export function renderHostTriggerValue(
  selected: SelectOption | SelectOption[] | null | undefined
) {
  const opt = Array.isArray(selected) ? selected[0] : selected;
  if (!opt) return null;
  const meta = getMeta(opt);
  const chip = getHostZoomChip(meta?.zoomStatus);
  const image = (opt as { image?: string }).image ?? meta?.image ?? undefined;
  return (
    <Flex alignItems="center" gap="2" minW={0}>
      <Avatar src={image} name={opt.label} size="sm" />
      <KText variant="small_text" noOfLines={1}>
        {opt.label}
      </KText>
      {chip && <Tag label={chip.label} variant={chip.variant} size="sm" />}
    </Flex>
  );
}
